import { useEffect, useMemo, useRef, useState } from "react";

import axios from "axios";

import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import {
  allFarmsByIdUrl,
  allBroilersUrl,
  dispatchStatusUrl,
  docDataUrl,
  farmsForUserUrl,
  hatcheryDataUrl,
  hatcheryDispatchUrl,
  removeDOCTableUrl,
  removeHatcheryDataUrl,
  updateDOCTableUrl,
  updateHatcheryDataUrl,
  updateHatcheryDispatchUrl,
} from "api/endpoints";
import { getStoredSession, hydrateStoredSession } from "utils/userIdentity";

const columns = [
  { key: "status", label: "", width: "1cm" },
  { key: "dispatchDate", label: "Dispatch Date", width: "3.84cm" },
  { key: "invoiceNumber", label: "Invoice No.", width: "3.6cm" },
  { key: "hatchery", label: "Hatchery", width: "4.32cm" },
  { key: "breederCode", label: "Breeder Code", width: "3.84cm" },
  { key: "dispatchStatus", label: "Dispatch Status", width: "4.2cm" },
  { key: "quantity", label: "Qty", width: "2.4cm" },
  { key: "open", label: "", width: "2cm" },
];

const unwrapCollection = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.value)) return payload.value;
  if (Array.isArray(payload?.result)) return payload.result;
  return [];
};

const getUserId = (session) =>
  session?.ID ?? session?.id ?? session?.userID ?? session?.userId ?? session?.UserID ?? null;

const formatDialogValue = (value) => (value == null ? "" : String(value));

const isValidCount = (value) => /^\d+$/.test(value) && Number.isSafeInteger(Number(value));
const isValidOptionalCount = (value) => value === "" || isValidCount(value);

const formatDateTime = (value) => {
  if (value == null || value === "") return "";
  const text = String(value);
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  return match ? `${match[3]}-${match[2]}-${match[1]} ${match[4]}:${match[5]}` : text;
};

const getDispatchStatusColor = (status) => {
  switch (Number(status)) {
    case 2:
    case 3:
      return "warning.main";
    case 4:
      return "success.main";
    case 5:
      return "error.main";
    default:
      return "grey.500";
  }
};

const mapDispatch = (dispatch, previous, statusNames = new Map(), hatcheryName) => {
  const status = statusNames.get(String(dispatch?.dispatchStatus));
  const unchangedStatus =
    String(dispatch?.dispatchStatus) === String(previous?.raw?.dispatchStatus);
  const statusLabel =
    status ??
    (unchangedStatus ? previous?.dispatchStatus : undefined) ??
    (Number(dispatch?.dispatchStatus) === 5 ? "Rejected" : dispatch?.dispatchStatus ?? "");
  return {
    ...previous,
    id: dispatch?.id ?? dispatch?.transactionId ?? previous?.id,
    dispatchDate: dispatch?.dispatchDate ?? "",
    invoiceNumber: dispatch?.invoiceNo ?? "",
    hatchery:
      hatcheryName ??
      (String(dispatch?.hatcheryId) === String(previous?.raw?.hatcheryId)
        ? previous?.hatchery
        : undefined) ??
      dispatch?.hatcheryId ??
      "",
    breederCode:
      dispatch?.breederCode ??
      (Array.isArray(dispatch?.dispatchDOC)
        ? dispatch.dispatchDOC
            .map((doc) => doc?.breederCode)
            .filter(Boolean)
            .join(", ")
        : ""),
    dispatchStatus: statusLabel,
    statusLabel,
    statusColor: getDispatchStatusColor(dispatch?.dispatchStatus),
    quantity: dispatch?.dispatchQty ?? "",
    raw: dispatch,
  };
};

const getStoredFarmId = () => {
  try {
    const selection = JSON.parse(localStorage.getItem("facilityLandingSelections") || "null");
    return selection?.farmId == null ? "" : String(selection.farmId);
  } catch {
    return "";
  }
};

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).length > 0);

const toNumber = (value) => {
  if (value === "" || value == null) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const getDocRecordId = (payload, transactionId) => {
  if (Array.isArray(payload)) {
    const matching = payload.find(
      (record) => String(record?.transactionId) === String(transactionId)
    );
    return getDocRecordId(matching, transactionId);
  }
  if (payload && typeof payload === "object") {
    const directId =
      payload.id ??
      payload.ID ??
      payload.docTableId ??
      payload.docId ??
      payload.insertedId ??
      payload.recordId;
    if (directId != null) return getDocRecordId(directId, transactionId);
    return getDocRecordId(payload.data ?? payload.value ?? payload.result, transactionId);
  }
  const id = toNumber(payload);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const findMatchingDocRecordId = (payload, doc, transactionId, invoiceNumber) => {
  const matches = unwrapCollection(payload).filter((record) => {
    if (String(record?.transactionId ?? "") !== String(transactionId)) return false;
    if (String(record?.invoiceNumber ?? "") !== String(invoiceNumber)) return false;
    return (
      String(record?.breederCode ?? "") === String(doc?.breederCode ?? "") &&
      String(record?.timeOfPulling ?? "") === String(doc?.timeOfPulling ?? "") &&
      String(record?.hatcherno ?? record?.hatcherNo ?? "") === String(doc?.hatchId ?? "") &&
      String(record?.setterno ?? record?.setterNo ?? "") === String(doc?.setterId ?? "")
    );
  });
  return matches.length === 1 ? getDocRecordId(matches[0], transactionId) : null;
};

const transactionsCacheKey = (userId, scope, id = "") =>
  `OmnioTransactions:v1:${userId}:${scope}:${id}`;

const readTransactionsCache = (key) => {
  if (!key || typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeTransactionsCache = (key, value) => {
  if (!key || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // The page still works when browser storage is unavailable or full.
  }
};

function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [farms, setFarms] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(() => {
    const userId = getUserId(getStoredSession());
    return userId == null ? null : String(userId);
  });
  const [selectedFarmId, setSelectedFarmId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const detailsRequestId = useRef(0);
  const displayedFarmIdRef = useRef("");
  const [deformity, setDeformity] = useState("");
  const [doa, setDoa] = useState("");
  const [dispatchDocRows, setDispatchDocRows] = useState([]);
  const [birdsPerBox, setBirdsPerBox] = useState("");
  const [noOfBoxes, setNoOfBoxes] = useState("");
  const [showCountErrors, setShowCountErrors] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [refreshCount, setRefreshCount] = useState(0);
  const [allocationOpen, setAllocationOpen] = useState(false);
  const [allocationLoading, setAllocationLoading] = useState(false);
  const allocationRequestId = useRef(0);
  const [allocationError, setAllocationError] = useState("");
  const [allocationFacilities, setAllocationFacilities] = useState([]);
  const [allocationCycles, setAllocationCycles] = useState([]);
  const allocationOptionsFarmRef = useRef("");
  const [allocationFacilityId, setAllocationFacilityId] = useState("");
  const [allocationCycleId, setAllocationCycleId] = useState("");
  const [submittedDocs, setSubmittedDocs] = useState({});
  const [pendingRejectionCleanup, setPendingRejectionCleanup] = useState(null);
  const [orderBy, setOrderBy] = useState("dispatchDate");
  const [order, setOrder] = useState("desc");
  const hasNestedDispatchDocs = Array.isArray(selectedTransaction?.raw?.dispatchDOC);
  const visibleDispatchDocs = hasNestedDispatchDocs
    ? dispatchDocRows
    : selectedTransaction?.raw
    ? [{ ...selectedTransaction.raw, deformities: deformity, doa }]
    : [];

  useEffect(() => {
    let active = true;

    const loadFarmOptions = async () => {
      setIsLoading(true);
      setLoadError("");

      try {
        const session = getStoredSession() ?? (await hydrateStoredSession());
        const userId = getUserId(session);
        if (userId == null || `${userId}`.trim() === "") {
          throw new Error("No logged-in user ID was found.");
        }
        if (!active) return;

        setCurrentUserId(String(userId));
        const farmCacheKey = transactionsCacheKey(userId, "farms");
        const cachedFarms = readTransactionsCache(farmCacheKey);
        if (active && Array.isArray(cachedFarms) && cachedFarms.length > 0) {
          setFarms(cachedFarms);
          setSelectedFarmId((current) => {
            const preferredId = current || getStoredFarmId();
            const selected = cachedFarms.find((farm) => String(farm.id) === preferredId);
            return String(selected?.id ?? cachedFarms[0]?.id ?? "");
          });
        }

        const farmsResponse = await axios.get(farmsForUserUrl(userId));

        if (!active) return;

        const farmOptions = unwrapCollection(farmsResponse.data)
          .map((farm) => ({
            id: farm?.farmId ?? farm?.FarmID ?? farm?.farmID ?? farm?.id ?? farm?.ID,
            name: farm?.name ?? farm?.farmName ?? farm?.FarmName,
          }))
          .filter((farm) => farm.id != null && farm.name);
        writeTransactionsCache(farmCacheKey, farmOptions);
        setFarms(farmOptions);
        setSelectedFarmId((current) => {
          const preferredId = current || getStoredFarmId();
          const selected = farmOptions.find((farm) => String(farm.id) === preferredId);
          return String(selected?.id ?? farmOptions[0]?.id ?? "");
        });
        if (farmOptions.length === 0) setIsLoading(false);
      } catch (error) {
        if (!active) return;
        setLoadError(error?.response?.data?.message || error?.message || "Unable to load farms.");
        setIsLoading(false);
      }
    };

    loadFarmOptions();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedFarmId) return undefined;
    let active = true;
    const cacheKey = currentUserId
      ? transactionsCacheKey(currentUserId, "dispatches", selectedFarmId)
      : null;
    const cachedTransactions = readTransactionsCache(cacheKey);
    if (Array.isArray(cachedTransactions)) {
      setTransactions(cachedTransactions);
    } else if (displayedFarmIdRef.current !== selectedFarmId) {
      setTransactions([]);
    }
    displayedFarmIdRef.current = selectedFarmId;

    const loadTransactions = async () => {
      setIsLoading(true);
      setLoadError("");

      try {
        const [response, statusResponse] = await Promise.all([
          axios.get(hatcheryDispatchUrl(selectedFarmId)),
          axios.get(dispatchStatusUrl()),
        ]);
        if (!active) return;

        const dispatches = unwrapCollection(response.data);
        const statusNames = new Map(
          unwrapCollection(statusResponse.data)
            .filter((status) => status?.id != null)
            .map((status) => [String(status.id), status.status])
        );
        const hatcheryIds = [...new Set(dispatches.map((dispatch) => dispatch?.hatcheryId))].filter(
          (id) => id != null && String(id).trim() !== ""
        );
        const hatcheryResults = await Promise.allSettled(
          hatcheryIds.map((hatcheryId) => axios.get(allFarmsByIdUrl(hatcheryId)))
        );
        if (!active) return;

        const hatcheryNames = new Map();
        hatcheryResults.forEach((result, index) => {
          if (result.status !== "fulfilled") return;
          const hatcheryId = String(hatcheryIds[index]);
          const farms = unwrapCollection(result.value.data);
          const hatchery = farms.find((farm) => String(farm?.id) === hatcheryId) || farms[0];
          if (hatchery?.name) hatcheryNames.set(hatcheryId, hatchery.name);
        });

        const nextTransactions = dispatches.map((dispatch, index) =>
          mapDispatch(
            dispatch,
            { id: index },
            statusNames,
            hatcheryNames.get(String(dispatch?.hatcheryId))
          )
        );
        setTransactions(nextTransactions);
        writeTransactionsCache(cacheKey, nextTransactions);
      } catch (error) {
        if (!active) return;
        setLoadError(
          error?.response?.data?.message || error?.message || "Unable to load DOC data."
        );
      } finally {
        if (active) setIsLoading(false);
      }
    };

    loadTransactions();
    return () => {
      active = false;
    };
  }, [selectedFarmId, refreshCount, currentUserId]);

  const showDispatchFormValues = (dispatch) => {
    setDeformity(formatDialogValue(dispatch?.deformities ?? dispatch?.deformity));
    setDoa(formatDialogValue(dispatch?.doa));
    setDispatchDocRows(
      Array.isArray(dispatch?.dispatchDOC)
        ? dispatch.dispatchDOC.map((doc) => ({
            ...doc,
            deformities: formatDialogValue(doc?.deformities),
            doa: formatDialogValue(doc?.doa),
          }))
        : []
    );
    setBirdsPerBox(formatDialogValue(dispatch?.birdsPerBox));
    setNoOfBoxes(formatDialogValue(dispatch?.noOfBoxes));
  };

  const openTransactionDetails = async (transaction) => {
    const requestId = ++detailsRequestId.current;
    setSelectedTransaction(transaction);
    showDispatchFormValues(transaction.raw);
    setDetailsLoading(true);
    setDetailsError("");
    setSaveError("");
    setShowCountErrors(false);

    try {
      const [response, statusResult] = await Promise.all([
        axios.get(hatcheryDispatchUrl(selectedFarmId)),
        axios.get(dispatchStatusUrl()).catch(() => null),
      ]);
      if (requestId !== detailsRequestId.current) return;
      const dispatches = unwrapCollection(response.data);
      const refreshed = dispatches.find(
        (dispatch) =>
          (dispatch?.id != null &&
            transaction.raw?.id != null &&
            String(dispatch.id) === String(transaction.raw.id)) ||
          (dispatch?.transactionId != null &&
            transaction.raw?.transactionId != null &&
            String(dispatch.transactionId) === String(transaction.raw.transactionId))
      );
      if (!refreshed) throw new Error("This dispatch was not found in the latest response.");

      let hatcheryName;
      if (String(refreshed.hatcheryId) !== String(transaction.raw?.hatcheryId)) {
        try {
          const hatcheryResponse = await axios.get(allFarmsByIdUrl(refreshed.hatcheryId));
          const farmsById = unwrapCollection(hatcheryResponse.data);
          hatcheryName =
            farmsById.find((farm) => String(farm?.id) === String(refreshed.hatcheryId))?.name ??
            farmsById[0]?.name;
        } catch {
          // The dispatch itself remains usable when a name lookup fails.
        }
      }
      if (requestId !== detailsRequestId.current) return;
      const statusNames = new Map(
        unwrapCollection(statusResult?.data)
          .filter((status) => status?.id != null)
          .map((status) => [String(status.id), status.status])
      );
      const updated = mapDispatch(refreshed, transaction, statusNames, hatcheryName);
      setSelectedTransaction(updated);
      setTransactions((current) =>
        current.map((row) => (row === transaction || row.id === transaction.id ? updated : row))
      );
      showDispatchFormValues(refreshed);
    } catch (error) {
      if (requestId !== detailsRequestId.current) return;
      setDetailsError(
        error?.response?.data?.message || error?.message || "Unable to refresh dispatch details."
      );
    } finally {
      if (requestId === detailsRequestId.current) setDetailsLoading(false);
    }
  };

  const closeTransactionDetails = () => {
    if (isSaving) return;
    detailsRequestId.current += 1;
    setSelectedTransaction(null);
    setDetailsError("");
  };

  const applyAllocationOptions = (facilities, cycles) => {
    setAllocationFacilities(facilities);
    setAllocationCycles(cycles);

    const storedSelection = (() => {
      try {
        return JSON.parse(localStorage.getItem("facilityLandingSelections") || "null");
      } catch {
        return null;
      }
    })();
    const storedFacility =
      String(storedSelection?.farmId) === String(selectedFarmId)
        ? facilities.find((facility) => String(facility.id) === String(storedSelection.facilityId))
        : null;
    const previouslySubmitted = submittedDocs[String(selectedTransaction?.raw?.transactionId)];
    const submittedFacility = facilities.find(
      (item) => String(item.id) === String(previouslySubmitted?.broilerId)
    );
    const facility = submittedFacility || storedFacility || facilities[0];
    const facilityCycles = cycles.filter(
      (cycle) => String(cycle.broilerId) === String(facility?.id)
    );
    const submittedCycle = facilityCycles.find(
      (cycle) => String(cycle.id) === String(previouslySubmitted?.cycleId)
    );
    const storedCycle = facilityCycles.find(
      (cycle) => String(cycle.id) === String(storedSelection?.cycleId)
    );
    setAllocationFacilityId(facility ? String(facility.id) : "");
    setAllocationCycleId(
      String(submittedCycle?.id ?? storedCycle?.id ?? facilityCycles[0]?.id ?? "")
    );
    if (facilities.length === 0) {
      setAllocationError("No Broiler Houses are available for this farm.");
    } else if (
      !cycles.some((cycle) =>
        facilities.some((item) => String(item.id) === String(cycle.broilerId))
      )
    ) {
      setAllocationError("No cycles are available for this farm's Broiler Houses.");
    }
  };

  const loadAllocationOptions = async () => {
    const requestId = ++allocationRequestId.current;
    setAllocationLoading(true);
    setAllocationError("");
    const cacheKey = currentUserId
      ? transactionsCacheKey(currentUserId, "allocation", selectedFarmId)
      : null;
    const cachedOptions = readTransactionsCache(cacheKey);
    if (Array.isArray(cachedOptions?.facilities) && Array.isArray(cachedOptions?.cycles)) {
      applyAllocationOptions(cachedOptions.facilities, cachedOptions.cycles);
    } else if (allocationOptionsFarmRef.current !== selectedFarmId) {
      setAllocationFacilities([]);
      setAllocationCycles([]);
      setAllocationFacilityId("");
      setAllocationCycleId("");
    }
    allocationOptionsFarmRef.current = selectedFarmId;

    try {
      const session = getStoredSession() ?? (await hydrateStoredSession());
      const userId = getUserId(session);
      if (userId == null || String(userId).trim() === "") {
        throw new Error("No logged-in user ID was found.");
      }

      const [farmsResponse, broilersResponse] = await Promise.all([
        axios.get(farmsForUserUrl(userId)),
        axios.get(allBroilersUrl()),
      ]);
      if (requestId !== allocationRequestId.current) return;

      const facilities = unwrapCollection(broilersResponse.data)
        .map((broiler) => ({
          id: pickFirst(
            broiler?.broilerID,
            broiler?.broilerId,
            broiler?.BroilerID,
            broiler?.facilityID,
            broiler?.facilityId,
            broiler?.id,
            broiler?.ID
          ),
          name: pickFirst(broiler?.name, broiler?.broilerName, broiler?.facilityName),
          farmId: pickFirst(broiler?.farmID, broiler?.farmId),
        }))
        .filter(
          (facility) =>
            facility.id != null &&
            facility.name &&
            String(facility.farmId) === String(selectedFarmId)
        );

      const farmRecords = Array.isArray(farmsResponse.data?.tbl_Cycles)
        ? [farmsResponse.data]
        : unwrapCollection(farmsResponse.data);
      const cycles = farmRecords
        .flatMap((farm) => (Array.isArray(farm?.tbl_Cycles) ? farm.tbl_Cycles : []))
        .map((cycle) => ({
          id: pickFirst(cycle?.id, cycle?.ID, cycle?.cycleId, cycle?.cycleID),
          name: pickFirst(
            cycle?.name,
            cycle?.cycleName,
            cycle?.CycleName,
            cycle?.cycle,
            cycle?.Cycle
          ),
          broilerId: pickFirst(
            cycle?.broilerid,
            cycle?.broilerId,
            cycle?.BroilerID,
            cycle?.facilityID,
            cycle?.facilityId
          ),
        }))
        .filter((cycle) => cycle.id != null && cycle.name && cycle.broilerId != null);

      applyAllocationOptions(facilities, cycles);
      writeTransactionsCache(cacheKey, { facilities, cycles });
    } catch (error) {
      if (requestId !== allocationRequestId.current) return;
      setAllocationError(
        typeof error?.response?.data?.message === "string"
          ? error.response.data.message
          : error?.message || "Unable to load facilities and cycles."
      );
    } finally {
      if (requestId === allocationRequestId.current) setAllocationLoading(false);
    }
  };

  const handleAllocationFacilityChange = (event) => {
    const facilityId = event.target.value;
    const firstCycle = allocationCycles.find(
      (cycle) => String(cycle.broilerId) === String(facilityId)
    );
    setAllocationFacilityId(facilityId);
    setAllocationCycleId(firstCycle ? String(firstCycle.id) : "");
  };

  const handleDispatchDecision = async (status) => {
    if (!selectedTransaction?.raw || isSaving) return;
    const dispatch = selectedTransaction.raw;
    const activeStatus = Number(dispatch.dispatchStatus);
    const transactionId = String(dispatch.transactionId);
    const retryRejectionCleanup =
      status === 5 && pendingRejectionCleanup?.transactionId === transactionId;
    const removeAcceptedInvoice = status === 5 && (activeStatus === 4 || retryRejectionCleanup);
    if (status === 4 && activeStatus === 4) return;
    if (status === 5 && activeStatus === 5 && !retryRejectionCleanup) return;
    const countsValid = hasNestedDispatchDocs
      ? (status !== 4 || dispatchDocRows.length > 0) &&
        dispatchDocRows.every((doc) => isValidCount(doc.deformities) && isValidCount(doc.doa))
      : isValidCount(deformity) && isValidCount(doa);
    if (!countsValid || !isValidOptionalCount(birdsPerBox) || !isValidOptionalCount(noOfBoxes)) {
      setShowCountErrors(true);
      return;
    }
    setIsSaving(true);
    setSaveError("");

    try {
      const updatedDocRows = dispatchDocRows.map((doc) => ({
        ...doc,
        deformities: Number(doc.deformities),
        doa: Number(doc.doa),
      }));
      const docUpdates = hasNestedDispatchDocs
        ? { dispatchDOC: updatedDocRows }
        : { deformities: Number(deformity), doa: Number(doa) };
      const dispatchPayload = {
        ...dispatch,
        dispatchStatus: status,
        ...docUpdates,
        birdsPerBox: toNumber(birdsPerBox),
        noOfBoxes: toNumber(noOfBoxes),
      };
      if (status !== 4 && !removeAcceptedInvoice) {
        await axios.post(updateHatcheryDispatchUrl(), dispatchPayload);
        setRefreshCount((count) => count + 1);
        window.dispatchEvent(new Event("doc-transactions-updated"));
      }
      if (status === 4) {
        setSelectedTransaction((current) =>
          current
            ? {
                ...current,
                raw: {
                  ...current.raw,
                  ...docUpdates,
                  birdsPerBox: toNumber(birdsPerBox),
                  noOfBoxes: toNumber(noOfBoxes),
                },
              }
            : current
        );
        setAllocationOpen(true);
        loadAllocationOptions();
      } else {
        if (removeAcceptedInvoice) {
          const linkedDocIds = retryRejectionCleanup
            ? pendingRejectionCleanup.docTableIds
            : Array.isArray(dispatch.dispatchDOC)
            ? [
                ...dispatch.dispatchDOC.map((doc) => getDocRecordId(doc.docTableId, transactionId)),
                ...(submittedDocs[transactionId]?.docTableIds ?? []).map((id) =>
                  getDocRecordId(id, transactionId)
                ),
                getDocRecordId(dispatch.docTableId, transactionId),
                getDocRecordId(submittedDocs[transactionId]?.docTableId, transactionId),
              ].filter((id) => id != null)
            : [
                getDocRecordId(
                  dispatch.docTableId ?? submittedDocs[transactionId]?.docTableId,
                  transactionId
                ),
              ].filter((id) => id != null);
          const docTableIds = [...new Set(linkedDocIds)];
          const docInvoiceId = retryRejectionCleanup
            ? pendingRejectionCleanup.docInvoiceId
            : getDocRecordId(dispatch.docInvoiceId, transactionId);
          for (let index = 0; index < docTableIds.length; index += 1) {
            try {
              await axios.post(removeDOCTableUrl(), { id: docTableIds[index] });
            } catch (error) {
              setPendingRejectionCleanup({
                transactionId,
                docTableIds: docTableIds.slice(index),
                docInvoiceId,
              });
              throw new Error(
                `A linked DOC record could not be removed: ${
                  error?.response?.data?.message || error?.message || "Request failed."
                } Please select Reject again to retry removal.`
              );
            }
          }
          if (docInvoiceId != null) {
            try {
              await axios.post(removeHatcheryDataUrl(), { id: docInvoiceId });
            } catch (error) {
              setPendingRejectionCleanup({ transactionId, docTableIds: [], docInvoiceId });
              throw new Error(
                `Linked DOC records were removed, but the invoice could not be removed: ${
                  error?.response?.data?.message || error?.message || "Request failed."
                } Please select Reject again to retry invoice removal.`
              );
            }
          }
          setPendingRejectionCleanup({ transactionId, docTableIds: [], docInvoiceId: null });
          await axios.post(updateHatcheryDispatchUrl(), {
            ...dispatchPayload,
            docInvoiceId: null,
            docTableId: null,
            ...(hasNestedDispatchDocs
              ? {
                  dispatchDOC: updatedDocRows.map((doc) => ({
                    ...doc,
                    docTableId: null,
                  })),
                }
              : {}),
          });
          setRefreshCount((count) => count + 1);
          window.dispatchEvent(new Event("doc-transactions-updated"));
          setSubmittedDocs((current) => {
            const next = { ...current };
            delete next[transactionId];
            return next;
          });
        }
        setPendingRejectionCleanup(null);
        setSelectedTransaction(null);
      }
    } catch (error) {
      setSaveError(
        typeof error?.response?.data?.message === "string"
          ? error.response.data.message
          : error?.message || "Unable to update dispatch status."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitAllocation = async () => {
    if (!selectedTransaction?.raw || isSaving) return;
    if (!allocationFacilityId || !allocationCycleId) {
      setAllocationError("Please select a Broiler House and Cycle.");
      return;
    }

    const dispatch = selectedTransaction.raw;
    const hasNestedDocs = Array.isArray(dispatch.dispatchDOC);
    const docs = hasNestedDocs ? dispatch.dispatchDOC : [dispatch];
    if (docs.length === 0) {
      setAllocationError("This invoice has no DOC detail rows to allocate.");
      return;
    }
    const docQuantities = docs.map(
      (doc) => doc.quantity ?? doc.qauntity ?? (docs.length === 1 ? dispatch.dispatchQty : null)
    );
    if (docQuantities.some((quantity) => !isValidCount(String(quantity ?? "")))) {
      setAllocationError("Each DOC detail row needs a whole-number quantity before allocation.");
      return;
    }
    const transactionKey = String(dispatch.transactionId);
    const previousSubmission = submittedDocs[transactionKey];
    const docTableIds = docs.map((doc, index) =>
      getDocRecordId(
        previousSubmission?.docTableIds?.[index] ??
          (index === 0 ? previousSubmission?.docTableId : null) ??
          doc.docTableId,
        dispatch.transactionId
      )
    );
    const postedDocIndexes = new Set(
      previousSubmission?.postedDocIndexes ?? (previousSubmission ? [0] : [])
    );
    if (
      previousSubmission &&
      (String(previousSubmission.farmId) !== String(selectedFarmId) ||
        String(previousSubmission.broilerId) !== String(allocationFacilityId) ||
        String(previousSubmission.cycleId) !== String(allocationCycleId))
    ) {
      setAllocationError("This DOC record has already been submitted for another allocation.");
      return;
    }

    const hatcheryPayload = {
      transactionId: dispatch.transactionId == null ? null : String(dispatch.transactionId),
      vcId: toNumber(dispatch.vcId),
      vcSectorType: 5,
      broilerId: toNumber(allocationFacilityId),
      cycleId: toNumber(allocationCycleId),
      hatcheryName: toNumber(dispatch.hatcheryId),
      invoiceNumber: dispatch.invoiceNo == null ? null : String(dispatch.invoiceNo),
      totalNoOfBoxes: toNumber(noOfBoxes),
      dispatchQty: toNumber(dispatch.dispatchQty),
      dispatchWeight: toNumber(dispatch.dispatchWeight),
      noOfBirdsInBoxes: toNumber(birdsPerBox),
      driverName: dispatch.driverName == null ? null : String(dispatch.driverName),
      transportReg: dispatch.transportReg == null ? null : String(dispatch.transportReg),
      tripStart: dispatch.tripStart ?? null,
      tripEnd: dispatch.tripEnd ?? null,
      date: dispatch.dispatchDate ?? null,
      timeOfDelivery: dispatch.tripEnd ?? null,
      tripTempStart: toNumber(dispatch.tripTempStart),
      tripTempEnd: toNumber(dispatch.tripTempEnd),
      tripHumStart: toNumber(dispatch.tripHumStart),
      tripHumEnd: toNumber(dispatch.tripHumEnd),
    };

    setIsSaving(true);
    setAllocationError("");
    let docPosted = postedDocIndexes.size > 0;
    let hatcheryLookupComplete = false;
    try {
      const hatcheryResponse = await axios.get(
        hatcheryDataUrl(allocationFacilityId, allocationCycleId)
      );
      hatcheryLookupComplete = true;
      const invoiceNumber = String(dispatch.invoiceNo ?? "").trim();
      const matchingRecords = unwrapCollection(hatcheryResponse.data).filter(
        (record) =>
          invoiceNumber !== "" &&
          String(record?.invoiceNumber ?? record?.InvoiceNumber ?? "").trim() === invoiceNumber
      );
      if (matchingRecords.length > 1) {
        throw new Error("Multiple Hatchery data records have this invoice number.");
      }
      const existingHatcheryRecord = matchingRecords[0];
      const existingHatcheryId = existingHatcheryRecord?.id ?? existingHatcheryRecord?.ID;
      if (existingHatcheryRecord && existingHatcheryId == null) {
        throw new Error("The matching Hatchery data record has no ID, so it cannot be updated.");
      }
      const hatcheryRecordPayload = existingHatcheryRecord
        ? { ...existingHatcheryRecord, ...hatcheryPayload, id: existingHatcheryId }
        : hatcheryPayload;

      const saveDocProgress = () => {
        setSubmittedDocs((current) => ({
          ...current,
          [transactionKey]: {
            farmId: selectedFarmId,
            broilerId: allocationFacilityId,
            cycleId: allocationCycleId,
            docTableId: docTableIds[0],
            docTableIds: [...docTableIds],
            postedDocIndexes: [...postedDocIndexes],
          },
        }));
      };
      for (let index = 0; index < docs.length; index += 1) {
        const doc = docs[index];
        if (!postedDocIndexes.has(index)) {
          const docPayload = {
            ...(docTableIds[index] != null ? { id: docTableIds[index] } : {}),
            transactionId: dispatch.transactionId,
            vcId: dispatch.vcId,
            farmId: toNumber(selectedFarmId),
            broilerId: toNumber(allocationFacilityId),
            cycleId: toNumber(allocationCycleId),
            invoiceNumber: dispatch.invoiceNo == null ? null : String(dispatch.invoiceNo),
            batchDate: dispatch.dispatchDate ?? null,
            breederCode: doc.breederCode ?? null,
            description: doc.description ?? null,
            chickWeight: toNumber(doc.chickWeight),
            qauntity: Number(docQuantities[index]),
            parentAge: toNumber(doc.parentAge),
            timeOfPulling: doc.timeOfPulling ?? null,
            hatcherno: toNumber(doc.hatchId),
            setterno: toNumber(doc.setterId),
            deformaties: Number(doc.deformities ?? deformity),
            doa: Number(doc.doa ?? doa),
          };
          const docResponse = await axios.post(updateDOCTableUrl(), docPayload);
          docPosted = true;
          postedDocIndexes.add(index);
          docTableIds[index] =
            getDocRecordId(docResponse.data, dispatch.transactionId) ?? docTableIds[index];
          saveDocProgress();
        }
        if (docTableIds[index] == null) {
          const docRecords = await axios.get(
            docDataUrl(allocationFacilityId, allocationCycleId, dispatch.invoiceNo)
          );
          docTableIds[index] =
            findMatchingDocRecordId(
              docRecords.data,
              doc,
              dispatch.transactionId,
              dispatch.invoiceNo
            ) ??
            (docs.length === 1 ? getDocRecordId(docRecords.data, dispatch.transactionId) : null);
          if (docTableIds[index] == null) {
            throw new Error(
              `The DOC record ID for row ${
                index + 1
              } was not returned or found. Please retry Submit.`
            );
          }
          saveDocProgress();
        }
      }
      const invoiceResponse = await axios.post(updateHatcheryDataUrl(), hatcheryRecordPayload);
      let docInvoiceId = getDocRecordId(existingHatcheryId, dispatch.transactionId);
      if (
        docInvoiceId == null &&
        invoiceResponse.data &&
        typeof invoiceResponse.data === "object"
      ) {
        docInvoiceId = getDocRecordId(invoiceResponse.data, dispatch.transactionId);
      }
      if (docInvoiceId == null) {
        const savedInvoiceResponse = await axios.get(
          hatcheryDataUrl(allocationFacilityId, allocationCycleId)
        );
        const savedInvoices = unwrapCollection(savedInvoiceResponse.data).filter(
          (record) =>
            invoiceNumber !== "" &&
            String(record?.invoiceNumber ?? record?.InvoiceNumber ?? "").trim() === invoiceNumber
        );
        if (savedInvoices.length !== 1) {
          throw new Error("The saved invoice ID could not be resolved. Please retry Submit.");
        }
        docInvoiceId = getDocRecordId(savedInvoices[0]?.id ?? savedInvoices[0]?.ID, transactionKey);
      }
      if (docInvoiceId == null) {
        throw new Error("The saved invoice has no ID, so it cannot be linked to the dispatch.");
      }
      await axios.post(updateHatcheryDispatchUrl(), {
        ...dispatch,
        dispatchStatus: 4,
        docInvoiceId,
        ...(hasNestedDocs
          ? { dispatchDOC: docs.map((doc, index) => ({ ...doc, docTableId: docTableIds[index] })) }
          : { deformities: Number(deformity), doa: Number(doa), docTableId: docTableIds[0] }),
        birdsPerBox: toNumber(birdsPerBox),
        noOfBoxes: toNumber(noOfBoxes),
      });
      setRefreshCount((count) => count + 1);
      window.dispatchEvent(new Event("doc-transactions-updated"));
      setAllocationOpen(false);
      setSelectedTransaction(null);
    } catch (error) {
      const message =
        typeof error?.response?.data?.message === "string"
          ? error.response.data.message
          : error?.message || "Request failed.";
      setAllocationError(
        !hatcheryLookupComplete
          ? `Unable to check existing Hatchery data: ${message}`
          : docPosted
          ? `DOC details may have been saved, but dispatch linking or Hatchery data could not be saved: ${message}`
          : `Unable to save DOC details: ${message}`
      );
    } finally {
      setIsSaving(false);
    }
  };

  const closeAllocation = () => {
    if (isSaving) return;
    allocationRequestId.current += 1;
    setAllocationOpen(false);
    setSelectedTransaction(null);
  };

  const selectedDocSubmission =
    submittedDocs[String(selectedTransaction?.raw?.transactionId)] || null;

  const handleRequestSort = (columnKey) => {
    const isAscending = orderBy === columnKey && order === "asc";
    setOrder(isAscending ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  const sortedTransactions = useMemo(() => {
    const compareValues = (first, second) => {
      if (orderBy === "dispatchDate") {
        const firstDate = Date.parse(first || "");
        const secondDate = Date.parse(second || "");

        if (!Number.isNaN(firstDate) && !Number.isNaN(secondDate)) {
          return firstDate - secondDate;
        }
      }

      return String(first ?? "").localeCompare(String(second ?? ""), undefined, {
        numeric: true,
        sensitivity: "base",
      });
    };

    return [...transactions].sort((first, second) => {
      const comparison = compareValues(first[orderBy], second[orderBy]);
      return order === "asc" ? comparison : -comparison;
    });
  }, [order, orderBy, transactions]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <MDBox mb={4} sx={{ width: { xs: "100%", sm: "20%" } }}>
          <Typography>Farm:</Typography>
          <Select
            fullWidth
            value={selectedFarmId}
            onChange={(event) => {
              detailsRequestId.current += 1;
              allocationRequestId.current += 1;
              setSelectedTransaction(null);
              setSelectedFarmId(event.target.value);
            }}
            disabled={farms.length === 0}
            sx={{
              height: "1cm",
              "& .MuiOutlinedInput-root, & .MuiInputBase-root": { height: "1cm !important" },
              "& .MuiSelect-select": {
                minHeight: "1cm !important",
                lineHeight: "1cm !important",
                display: "flex",
                alignItems: "center",
                py: 0,
              },
            }}
          >
            {farms.map((farm) => (
              <MenuItem key={farm.id} value={String(farm.id)}>
                {farm.name}
              </MenuItem>
            ))}
          </Select>
        </MDBox>
        <Card sx={{ overflow: "hidden" }}>
          <MDBox p={2}>
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDTypography variant="h6">DOC</MDTypography>
              {isLoading && transactions.length > 0 && (
                <CircularProgress color="info" size={16} aria-label="Refreshing DOC transactions" />
              )}
            </MDBox>
            {loadError && transactions.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Showing cached DOC transactions. {loadError}
              </Alert>
            )}

            <TableContainer
              component={MDBox}
              sx={{
                mt: 2,
                boxShadow: "none",
                width: "fit-content",
                maxWidth: "100%",
                mx: "auto",
                overflowX: "auto",
              }}
            >
              <Table
                size="small"
                aria-label="DOC transactions table"
                sx={{
                  tableLayout: "fixed",
                  width: "25.2cm",
                  minWidth: "25.2cm",
                  borderCollapse: "collapse",
                  "& th, & td": {
                    border: "1px solid",
                    borderColor: "grey.300",
                    fontSize: "0.9rem",
                    lineHeight: 1.2,
                    p: 0.5,
                    textAlign: "center",
                    color: "text.primary",
                  },
                }}
              >
                <colgroup>
                  {columns.map((column) => (
                    <col key={column.key} style={{ width: column.width }} />
                  ))}
                </colgroup>
                <TableBody>
                  <TableRow sx={{ height: "0.96cm", bgcolor: "grey.100" }}>
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        aria-label={column.key === "status" ? "Transaction status" : undefined}
                        sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                      >
                        {["dispatchDate", "dispatchStatus"].includes(column.key) ? (
                          <TableSortLabel
                            active={orderBy === column.key}
                            direction={orderBy === column.key ? order : "asc"}
                            onClick={() => handleRequestSort(column.key)}
                            sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {isLoading && sortedTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center" sx={{ py: 2 }}>
                        <CircularProgress color="info" size={24} />
                      </TableCell>
                    </TableRow>
                  ) : loadError && sortedTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        align="center"
                        sx={{ py: 2, color: "error.main" }}
                      >
                        {loadError}
                      </TableCell>
                    </TableRow>
                  ) : sortedTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center" sx={{ py: 2 }}>
                        No DOC transactions available.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedTransactions.map((transaction) => (
                      <TableRow key={transaction.id} sx={{ height: "0.64cm" }}>
                        <TableCell>
                          <MDBox
                            component="span"
                            title={transaction.statusLabel || "Status pending"}
                            aria-label={transaction.statusLabel || "Status pending"}
                            sx={{
                              display: "inline-block",
                              width: "0.28cm",
                              height: "0.28cm",
                              borderRadius: "50%",
                              bgcolor: transaction.statusColor || "grey.500",
                              verticalAlign: "middle",
                            }}
                          />
                        </TableCell>
                        <TableCell>{formatDateTime(transaction.dispatchDate)}</TableCell>
                        <TableCell>{transaction.invoiceNumber}</TableCell>
                        <TableCell>{transaction.hatchery}</TableCell>
                        <TableCell>{transaction.breederCode}</TableCell>
                        <TableCell>{transaction.dispatchStatus}</TableCell>
                        <TableCell>{transaction.quantity}</TableCell>
                        <TableCell>
                          <Link
                            component="button"
                            type="button"
                            color="info"
                            underline="hover"
                            onClick={() => openTransactionDetails(transaction)}
                          >
                            Details
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        </Card>
      </MDBox>
      <Dialog
        open={Boolean(selectedTransaction)}
        onClose={closeTransactionDetails}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Hatchery Dispatch</DialogTitle>
        <DialogContent dividers>
          {detailsLoading && selectedTransaction?.raw && (
            <MDBox display="flex" alignItems="center" gap={1} mb={2}>
              <CircularProgress color="info" size={16} />
              <Typography variant="body2">Refreshing dispatch details…</Typography>
            </MDBox>
          )}
          {detailsError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button onClick={() => openTransactionDetails(selectedTransaction)}>Retry</Button>
              }
            >
              {detailsError}
            </Alert>
          )}
          {!selectedTransaction?.raw ? (
            <MDBox display="flex" justifyContent="center" py={4}>
              <CircularProgress color="info" size={28} />
            </MDBox>
          ) : (
            <MDBox
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, calc(25% - 4px)))",
                gap: 2,
                "& > .MuiTextField-root": { width: "100%", justifySelf: "start" },
                "& > :not(.MuiTextField-root)": { width: "calc(200% - 16px)" },
              }}
            >
              <MDBox
                sx={{
                  gridColumn: "1 / -1",
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                  gap: 2,
                  "& > .MuiTextField-root": { width: "100%" },
                }}
              >
                <TextField
                  label="Invoice No."
                  value={formatDialogValue(selectedTransaction?.raw?.invoiceNo)}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Hatchery"
                  value={formatDialogValue(selectedTransaction?.hatchery)}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Transaction ID"
                  value={formatDialogValue(selectedTransaction?.raw?.transactionId)}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Value Chain ID"
                  value={formatDialogValue(selectedTransaction?.raw?.vcId)}
                  InputProps={{ readOnly: true }}
                />
              </MDBox>

              <MDTypography
                variant="button"
                fontWeight="medium"
                sx={{ gridColumn: "1 / -1", bgcolor: "grey.200", px: 1, py: 0.75 }}
              >
                Dispatch Details
              </MDTypography>
              <MDBox
                sx={{
                  gridColumn: "1 / -1",
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                  gap: 2,
                  "& > .MuiTextField-root": { width: "100%" },
                }}
              >
                <TextField
                  label="Dispatch Date"
                  value={formatDateTime(selectedTransaction?.raw?.dispatchDate)}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Dispatch Status"
                  value={formatDialogValue(selectedTransaction?.dispatchStatus)}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Dispatch Qty"
                  value={formatDialogValue(selectedTransaction?.raw?.dispatchQty)}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Dispatch Weight"
                  value={formatDialogValue(selectedTransaction?.raw?.dispatchWeight)}
                  InputProps={{ readOnly: true }}
                />
              </MDBox>
              <TextField
                label="Bird per Box"
                type="number"
                disabled={detailsLoading}
                value={birdsPerBox}
                onChange={(event) => setBirdsPerBox(event.target.value)}
                inputProps={{ min: 0, step: 1 }}
                error={showCountErrors && !isValidOptionalCount(birdsPerBox)}
                helperText={
                  showCountErrors && !isValidOptionalCount(birdsPerBox)
                    ? "Enter a whole number of 0 or more"
                    : ""
                }
              />
              <TextField
                label="No. of Boxes"
                type="number"
                disabled={detailsLoading}
                value={noOfBoxes}
                onChange={(event) => setNoOfBoxes(event.target.value)}
                inputProps={{ min: 0, step: 1 }}
                error={showCountErrors && !isValidOptionalCount(noOfBoxes)}
                helperText={
                  showCountErrors && !isValidOptionalCount(noOfBoxes)
                    ? "Enter a whole number of 0 or more"
                    : ""
                }
              />

              <MDTypography
                variant="button"
                fontWeight="medium"
                sx={{ gridColumn: "1 / -1", bgcolor: "grey.200", px: 1, py: 0.75 }}
              >
                DOC Details
              </MDTypography>
              <TableContainer
                component={MDBox}
                sx={{
                  gridColumn: "1 / -1",
                  boxShadow: "none",
                  maxWidth: "none",
                  overflowX: "auto",
                }}
              >
                <Table
                  size="small"
                  aria-label="Hatchery Dispatch DOC Details"
                  sx={{
                    tableLayout: "fixed",
                    width: "25cm",
                    minWidth: "25cm",
                    borderCollapse: "collapse",
                    "& th, & td": {
                      border: "1px solid",
                      borderColor: "grey.300",
                      fontSize: "0.8rem",
                      lineHeight: 1.2,
                      p: 0.75,
                      textAlign: "center",
                    },
                  }}
                >
                  <TableBody>
                    <TableRow sx={{ height: "1.5cm", bgcolor: "grey.100" }}>
                      {[
                        "Breeder Code",
                        "Parent Age",
                        "Chick Weight",
                        "Description",
                        "Pulling Time",
                        "Vaccination",
                        "Setter ID",
                        "Hatcher ID",
                        "Deformities",
                        "DOA",
                      ].map((heading) => (
                        <TableCell key={heading} sx={{ fontWeight: 700 }}>
                          {heading}
                        </TableCell>
                      ))}
                    </TableRow>
                    {visibleDispatchDocs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} sx={{ py: 2, color: "text.secondary" }}>
                          No DOC details available.
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibleDispatchDocs.map((doc, index) => (
                        <TableRow key={doc.id ?? index}>
                          <TableCell>{formatDialogValue(doc.breederCode)}</TableCell>
                          <TableCell>{formatDialogValue(doc.parentAge)}</TableCell>
                          <TableCell>{formatDialogValue(doc.chickWeight)}</TableCell>
                          <TableCell>{formatDialogValue(doc.description)}</TableCell>
                          <TableCell>{formatDateTime(doc.timeOfPulling)}</TableCell>
                          <TableCell>{formatDialogValue(doc.vaccination)}</TableCell>
                          <TableCell>{formatDialogValue(doc.setterId)}</TableCell>
                          <TableCell>{formatDialogValue(doc.hatchId)}</TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              required
                              disabled={detailsLoading}
                              aria-label={`Deformities for DOC row ${index + 1}`}
                              value={doc.deformities}
                              onChange={(event) => {
                                if (hasNestedDispatchDocs) {
                                  setDispatchDocRows((current) =>
                                    current.map((row, rowIndex) =>
                                      rowIndex === index
                                        ? { ...row, deformities: event.target.value }
                                        : row
                                    )
                                  );
                                } else {
                                  setDeformity(event.target.value);
                                }
                              }}
                              inputProps={{ min: 0, step: 1 }}
                              error={showCountErrors && !isValidCount(doc.deformities)}
                              sx={{ width: "2cm" }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              required
                              disabled={detailsLoading}
                              aria-label={`DOA for DOC row ${index + 1}`}
                              value={doc.doa}
                              onChange={(event) => {
                                if (hasNestedDispatchDocs) {
                                  setDispatchDocRows((current) =>
                                    current.map((row, rowIndex) =>
                                      rowIndex === index ? { ...row, doa: event.target.value } : row
                                    )
                                  );
                                } else {
                                  setDoa(event.target.value);
                                }
                              }}
                              inputProps={{ min: 0, step: 1 }}
                              error={showCountErrors && !isValidCount(doc.doa)}
                              sx={{ width: "2cm" }}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {showCountErrors && hasNestedDispatchDocs && visibleDispatchDocs.length === 0 && (
                <Alert severity="error" sx={{ gridColumn: "1 / -1" }}>
                  At least one DOC detail row is required to accept this dispatch.
                </Alert>
              )}
              {showCountErrors &&
                visibleDispatchDocs.some(
                  (doc) => !isValidCount(doc.deformities) || !isValidCount(doc.doa)
                ) && (
                  <Alert severity="error" sx={{ gridColumn: "1 / -1" }}>
                    Enter a whole number of 0 or more for every Deformities and DOA field.
                  </Alert>
                )}

              <MDTypography
                variant="button"
                fontWeight="medium"
                sx={{ gridColumn: "1 / -1", bgcolor: "grey.200", px: 1, py: 0.75 }}
              >
                Transport Details
              </MDTypography>
              <MDBox
                sx={{
                  gridColumn: "1 / -1",
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 2,
                  "& > .MuiTextField-root": { width: "100%" },
                }}
              >
                <TextField
                  label="Driver Name"
                  value={formatDialogValue(selectedTransaction?.raw?.driverName)}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Temp Start"
                  value={formatDialogValue(selectedTransaction?.raw?.tripTempStart)}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Hum Start"
                  value={formatDialogValue(selectedTransaction?.raw?.tripHumStart)}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Trip Start"
                  value={formatDateTime(selectedTransaction?.raw?.tripStart)}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Transport Reg"
                  value={formatDialogValue(selectedTransaction?.raw?.transportReg)}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Temp End"
                  value={formatDialogValue(selectedTransaction?.raw?.tripTempEnd)}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Hum End"
                  value={formatDialogValue(selectedTransaction?.raw?.tripHumEnd)}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Trip End"
                  value={formatDateTime(selectedTransaction?.raw?.tripEnd)}
                  InputProps={{ readOnly: true }}
                />
              </MDBox>
            </MDBox>
          )}
        </DialogContent>
        {saveError && (
          <Alert severity="error" sx={{ mx: 3, mt: 2 }}>
            {saveError}
          </Alert>
        )}
        <DialogActions>
          <Button
            variant="contained"
            disabled={
              isSaving ||
              detailsLoading ||
              Boolean(detailsError) ||
              Number(selectedTransaction?.raw?.dispatchStatus) === 4
            }
            onClick={() => handleDispatchDecision(4)}
            sx={{ color: "#fff", "&:hover": { color: "#fff" } }}
          >
            Accept
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={
              isSaving ||
              detailsLoading ||
              Boolean(detailsError) ||
              (Number(selectedTransaction?.raw?.dispatchStatus) === 5 &&
                pendingRejectionCleanup?.transactionId !==
                  String(selectedTransaction?.raw?.transactionId))
            }
            onClick={() => handleDispatchDecision(5)}
            sx={{ color: "#424242", "&:hover": { color: "#fff", bgcolor: "warning.main" } }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={allocationOpen} onClose={closeAllocation} fullWidth maxWidth="sm">
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Please select a Broiler House and Cycle to allocate your DOCs. Then select Submit.
          </Alert>
          {selectedDocSubmission && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              DOC details may be saved. Submit will retry the remaining allocation steps.
            </Alert>
          )}
          {allocationLoading && allocationFacilities.length > 0 && (
            <MDBox display="flex" alignItems="center" gap={1} mb={2}>
              <CircularProgress color="info" size={16} />
              <Typography variant="body2">Refreshing Broiler Houses and Cycles…</Typography>
            </MDBox>
          )}
          {allocationLoading && allocationFacilities.length === 0 ? (
            <MDBox display="flex" justifyContent="center" py={3}>
              <CircularProgress color="info" size={24} />
            </MDBox>
          ) : (
            <MDBox
              sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}
            >
              <MDBox>
                <Typography>Facility:</Typography>
                <Select
                  fullWidth
                  value={allocationFacilityId}
                  onChange={handleAllocationFacilityChange}
                  disabled={allocationLoading || Boolean(selectedDocSubmission)}
                  sx={{ height: "1cm" }}
                >
                  {allocationFacilities.map((facility) => (
                    <MenuItem key={facility.id} value={String(facility.id)}>
                      {facility.name}
                    </MenuItem>
                  ))}
                </Select>
              </MDBox>
              <MDBox>
                <Typography>Cycle:</Typography>
                <Select
                  fullWidth
                  value={allocationCycleId}
                  onChange={(event) => setAllocationCycleId(event.target.value)}
                  disabled={allocationLoading || Boolean(selectedDocSubmission)}
                  sx={{ height: "1cm" }}
                >
                  {allocationCycles
                    .filter((cycle) => String(cycle.broilerId) === allocationFacilityId)
                    .map((cycle) => (
                      <MenuItem key={cycle.id} value={String(cycle.id)}>
                        {cycle.name}
                      </MenuItem>
                    ))}
                </Select>
              </MDBox>
            </MDBox>
          )}
          {allocationError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {allocationError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={isSaving} onClick={closeAllocation}>
            Cancel
          </Button>
          {allocationError && !allocationLoading && allocationFacilities.length === 0 && (
            <Button disabled={isSaving} onClick={loadAllocationOptions}>
              Retry
            </Button>
          )}
          <Button
            variant="contained"
            disabled={allocationLoading || isSaving || !allocationFacilityId || !allocationCycleId}
            onClick={handleSubmitAllocation}
            sx={{ color: "#fff", "&:hover": { color: "#fff" } }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default Transactions;
